// Client filtering only. Lifecycle and control reasons are supplied by the server.
// Keep the web/native copies identical; the parity test checks the source.
export type FilterShipment = {
  status: string;
  effective_status?: string;
  last_location?: { timestamp?: string } | null;
  operational?: {
    effective_status: string;
    active: boolean;
    in_transit: boolean;
    control_tasks: { code: string }[];
  } | null;
};

export type RegistryFilter = 'all' | 'waiting' | 'in_progress' | 'attention';
export type OperationalFilter = 'in_progress' | 'no_geo' | 'attention';

export function parseOperationalFilter(value: string | null | undefined): OperationalFilter {
  if (value === 'no_geo' || value === 'no_geolocation') return 'no_geo';
  return value === 'attention' ? 'attention' : 'in_progress';
}

// missing_documents is currently raised only after completion; needs_review is age-only.
export function isOperationalTask(task: { code: string }) {
  return ['no_geolocation', 'stale_geolocation', 'completion_requested'].includes(task.code);
}

export function isOperationalShipment(shipment: FilterShipment) {
  const status = shipment.operational?.effective_status || shipment.effective_status || shipment.status;
  if (['completed', 'delivered', 'cancelled', 'rejected'].includes(status)) return false;
  return shipment.operational
    ? shipment.operational.active && shipment.operational.in_transit
    : status === 'in_progress';
}

export function isGeolocationTask(task: { code: string }) {
  return task.code === 'no_geolocation' || task.code === 'stale_geolocation';
}

export function matchesOperationalFilter(shipment: FilterShipment, filter: OperationalFilter) {
  if (!isOperationalShipment(shipment)) return false;
  const tasks = shipment.operational?.control_tasks ?? [];
  if (filter === 'attention') return tasks.some(isOperationalTask);
  if (filter === 'no_geo') return !shipment.last_location?.timestamp || tasks.some(isGeolocationTask);
  return true;
}

export function matchesRegistryFilter(shipment: FilterShipment, filter: RegistryFilter) {
  if (filter === 'all') {
    const status = shipment.operational?.effective_status || shipment.effective_status || shipment.status;
    const active = shipment.operational?.active
      ?? ['created', 'pending', 'assigned', 'awaiting_driver', 'driver_rejected', 'accepted', 'in_progress'].includes(status);
    return active || (shipment.operational?.control_tasks.length ?? 0) > 0;
  }
  if (filter === 'attention') return (shipment.operational?.control_tasks.length ?? 0) > 0;
  if (filter === 'in_progress') return isOperationalShipment(shipment);
  const status = shipment.operational?.effective_status || shipment.effective_status || shipment.status;
  return shipment.operational?.active !== false
    && ['created', 'pending', 'assigned', 'awaiting_driver', 'driver_rejected'].includes(status);
}
